import type {
	EnableCustomCategorySettingArgs,
	EnableCustomCategorySettingResult,
	GetEditCustomerPageResult,
	SaveCustomerAttributesArgs,
	SaveCustomerAttributesResult,
} from '@ogm3/server-types';
import type { RemoteData } from '../async-actions';
import { createFetchAction } from '../async-actions';
import { toastr } from '../helpers/Toastr';
import {
	EnableCustomCategorySetting,
	GetEditCustomerPage,
	SaveCustomerAttributes,
} from '../remote/hubs/Web_Controllers_V3_SyscoSelfService_ManageCustomersController';

// ======
// State
// ======

export interface EditCustomerViewModel {
	customerName: string;
	nationalId: string;
	specIndicator: boolean;
	autoPhaseOut: boolean;
	syncPropItems: boolean;
	dailyRefresh: boolean;
	isSaving: boolean;
	sysco12Default: CustomCategoryOptionSetting;
	fullCustom: CustomCategoryOptionSetting;
}

export interface EditCustomerState {
	viewModel: RemoteData<EditCustomerViewModel, string[]>;
	customerId: number;
}

export type ToggleField =
	| 'specIndicator'
	| 'autoPhaseOut'
	| 'syncPropItems'
	| 'dailyRefresh';
export type CustomCategorySetting = 'sysco12Default' | 'fullCustom';
export type CustomCategoryOptionSetting =
	| 'available'
	| 'enabled'
	| 'disabled'
	| 'confirm'
	| 'confirm-pending';

export function initialState(): EditCustomerState {
	return {
		viewModel: { type: 'initial' },
		customerId: -1,
	};
}

export function EditCustomerReducer(
	state: EditCustomerState = initialState(),
	action: ExampleAction
): EditCustomerState {
	switch (action.type) {
		case '@customers:EDIT_CUSTOMER_PAGE_LOADED_PENDING': {
			const customerId = action.payload;
			return {
				...initialState(),
				customerId,
			};
		}
		case '@customers:EDIT_CUSTOMER_PAGE_LOADED_SUCCESS': {
			return {
				...state,
				viewModel: editCustomerResultToViewModel(action.payload),
				customerId: action.payload.customerId,
			};
		}
	}

	if (state.viewModel.type !== 'success') {
		return state;
	}

	switch (action.type) {
		case '@customers:TOGGLE_CHANGED': {
			const nextData = { ...state.viewModel.data };
			nextData[action.payload] = !nextData[action.payload];

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOM_CATEGORY_SETTING_ENABLED': {
			const nextData = { ...state.viewModel.data };
			nextData[action.payload] = 'confirm';

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOM_CATEGORY_SETTING_CANCELLED': {
			const nextData = { ...state.viewModel.data };
			nextData.fullCustom = 'available';
			nextData.sysco12Default = 'available';

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOMER_ATTRIBUTES_SAVED_PENDING': {
			const nextData = { ...state.viewModel.data };
			nextData.isSaving = true;

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOMER_ATTRIBUTES_SAVED_FAILURE': {
			const nextData = { ...state.viewModel.data };
			nextData.isSaving = false;

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOMER_ATTRIBUTES_SAVED_SUCCESS': {
			const nextData = { ...state.viewModel.data };
			nextData.isSaving = false;

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_PENDING': {
			const nextData = { ...state.viewModel.data };
			nextData[action.payload] = 'confirm-pending';

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_SUCCESS': {
			const nextData = { ...state.viewModel.data };
			if (action.payload.isFullCustom) {
				nextData.fullCustom = 'enabled';
				nextData.sysco12Default = 'disabled';
			}
			if (action.payload.isSysco12Default) {
				nextData.fullCustom = 'disabled';
				nextData.sysco12Default = 'enabled';
			}

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
	}

	return state;
}

function editCustomerResultToViewModel(
	args: GetEditCustomerPageResult
): EditCustomerState['viewModel'] {
	let fullCustom: CustomCategoryOptionSetting = 'disabled';
	let sysco12Default: CustomCategoryOptionSetting = 'disabled';

	// Let the user pick either one if nothing is enabled.
	if (!args.isFullCustom && !args.isSysco12Default) {
		fullCustom = 'available';
		sysco12Default = 'available';
	}

	// Otherwise, enable one and disable the other.
	if (args.isFullCustom && !args.isSysco12Default) {
		fullCustom = 'enabled';
		sysco12Default = 'disabled';
	}
	if (!args.isFullCustom && args.isSysco12Default) {
		fullCustom = 'disabled';
		sysco12Default = 'enabled';
	}

	return {
		type: 'success',
		data: {
			customerName: args.customerName,
			nationalId: args.nationalId,
			isSaving: false,
			autoPhaseOut: args.hasAutoPhaseOut,
			dailyRefresh: args.hasDailyRefresh,
			specIndicator: args.hasSpecIndicator,
			syncPropItems: args.hasSyncPropItems,
			fullCustom,
			sysco12Default,
		},
	};
}

// ========
// Actions
// ========

export type ExampleAction =
	| EDIT_CUSTOMER_PAGE_LOADED_PENDING
	| EDIT_CUSTOMER_PAGE_LOADED_FAILURE
	| EDIT_CUSTOMER_PAGE_LOADED_SUCCESS
	| TOGGLE_CHANGED
	| CUSTOM_CATEGORY_SETTING_ENABLED
	| CUSTOM_CATEGORY_SETTING_CANCELLED
	| CUSTOMER_ATTRIBUTES_SAVED_PENDING
	| CUSTOMER_ATTRIBUTES_SAVED_FAILURE
	| CUSTOMER_ATTRIBUTES_SAVED_SUCCESS
	| CUSTOM_CATEGORY_SETTING_CONFIRMED_PENDING
	| CUSTOM_CATEGORY_SETTING_CONFIRMED_FAILURE
	| CUSTOM_CATEGORY_SETTING_CONFIRMED_SUCCESS;

export interface EDIT_CUSTOMER_PAGE_LOADED_PENDING {
	type: '@customers:EDIT_CUSTOMER_PAGE_LOADED_PENDING';
	payload: number;
}
export interface EDIT_CUSTOMER_PAGE_LOADED_FAILURE {
	type: '@customers:EDIT_CUSTOMER_PAGE_LOADED_FAILURE';
	errors: string[];
}
export interface EDIT_CUSTOMER_PAGE_LOADED_SUCCESS {
	type: '@customers:EDIT_CUSTOMER_PAGE_LOADED_SUCCESS';
	payload: GetEditCustomerPageResult;
}

export interface EditCustomerPageLoadedArgs {
	customerId: number;
}

export const editCustomerPageLoaded = createFetchAction<
	EditCustomerPageLoadedArgs,
	EDIT_CUSTOMER_PAGE_LOADED_PENDING,
	EDIT_CUSTOMER_PAGE_LOADED_FAILURE,
	EDIT_CUSTOMER_PAGE_LOADED_SUCCESS
>(
	'@customers:EDIT_CUSTOMER_PAGE_LOADED_PENDING',
	'@customers:EDIT_CUSTOMER_PAGE_LOADED_FAILURE',
	'@customers:EDIT_CUSTOMER_PAGE_LOADED_SUCCESS',
	(args) => GetEditCustomerPage(args.customerId),
	(args) => args.customerId
);

export interface TOGGLE_CHANGED {
	type: '@customers:TOGGLE_CHANGED';
	payload: ToggleField;
}

export function toggleChanged(field: ToggleField): TOGGLE_CHANGED {
	return {
		type: '@customers:TOGGLE_CHANGED',
		payload: field,
	};
}

export interface CUSTOM_CATEGORY_SETTING_ENABLED {
	type: '@customers:CUSTOM_CATEGORY_SETTING_ENABLED';
	payload: CustomCategorySetting;
}

export function customCategorySettingEnabled(
	setting: CustomCategorySetting
): CUSTOM_CATEGORY_SETTING_ENABLED {
	return {
		type: '@customers:CUSTOM_CATEGORY_SETTING_ENABLED',
		payload: setting,
	};
}

export interface CUSTOM_CATEGORY_SETTING_CANCELLED {
	type: '@customers:CUSTOM_CATEGORY_SETTING_CANCELLED';
}

export function customCategorySettingCancelled(): CUSTOM_CATEGORY_SETTING_CANCELLED {
	return {
		type: '@customers:CUSTOM_CATEGORY_SETTING_CANCELLED',
	};
}

export interface CUSTOMER_ATTRIBUTES_SAVED_PENDING {
	type: '@customers:CUSTOMER_ATTRIBUTES_SAVED_PENDING';
}
export interface CUSTOMER_ATTRIBUTES_SAVED_FAILURE {
	type: '@customers:CUSTOMER_ATTRIBUTES_SAVED_FAILURE';
	errors: string[];
}
export interface CUSTOMER_ATTRIBUTES_SAVED_SUCCESS {
	type: '@customers:CUSTOMER_ATTRIBUTES_SAVED_SUCCESS';
	payload: SaveCustomerAttributesResult;
}

export const customerAttributesSaved = createFetchAction<
	{},
	CUSTOMER_ATTRIBUTES_SAVED_PENDING,
	CUSTOMER_ATTRIBUTES_SAVED_FAILURE,
	CUSTOMER_ATTRIBUTES_SAVED_SUCCESS
>(
	'@customers:CUSTOMER_ATTRIBUTES_SAVED_PENDING',
	'@customers:CUSTOMER_ATTRIBUTES_SAVED_FAILURE',
	'@customers:CUSTOMER_ATTRIBUTES_SAVED_SUCCESS',
	(args, dispatch, getState) => {
		const { editCustomer } = getState();
		const vm = editCustomer.viewModel;

		if (vm.type !== 'success') {
			throw new Error('page not loaded');
		}

		const apiArgs: SaveCustomerAttributesArgs = {
			customerId: editCustomer.customerId,
			hasAutoPhaseOut: vm.data.autoPhaseOut,
			hasDailyRefresh: vm.data.dailyRefresh,
			hasSpecIndicator: vm.data.specIndicator,
			hasSyncPropItems: vm.data.syncPropItems,
		};

		return SaveCustomerAttributes(apiArgs).then((res) => {
			if (res.kind === 'success') {
				toastr.success({ title: 'Success', body: 'Customer attributes saved' });
			}

			if (res.kind === 'failure') {
				toastr.error({
					title: 'Could not save attributes',
					body: res.errors.join(', '),
				});
			}

			return res;
		});
	}
);

export interface CUSTOM_CATEGORY_SETTING_CONFIRMED_PENDING {
	type: '@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_PENDING';
	payload: CustomCategorySetting;
}
export interface CUSTOM_CATEGORY_SETTING_CONFIRMED_FAILURE {
	type: '@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_FAILURE';
	errors: string[];
}
export interface CUSTOM_CATEGORY_SETTING_CONFIRMED_SUCCESS {
	type: '@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_SUCCESS';
	payload: EnableCustomCategorySettingResult;
}

export const customCategorySettingConfirmed = createFetchAction<
	CustomCategorySetting,
	CUSTOM_CATEGORY_SETTING_CONFIRMED_PENDING,
	CUSTOM_CATEGORY_SETTING_CONFIRMED_FAILURE,
	CUSTOM_CATEGORY_SETTING_CONFIRMED_SUCCESS
>(
	'@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_PENDING',
	'@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_FAILURE',
	'@customers:CUSTOM_CATEGORY_SETTING_CONFIRMED_SUCCESS',
	(args, dispatch, getState) => {
		const { editCustomer } = getState();
		const vm = editCustomer.viewModel;

		if (vm.type !== 'success') {
			throw new Error('page not loaded');
		}

		const apiArgs: EnableCustomCategorySettingArgs = {
			customerId: editCustomer.customerId,
			isFullCustom: args === 'fullCustom',
			isSysco12Default: args === 'sysco12Default',
		};

		return EnableCustomCategorySetting(apiArgs).then((res) => {
			if (res.kind === 'success') {
				toastr.success({
					title: 'Success',
					body: 'Custom category setting saved',
				});
			}

			if (res.kind === 'failure') {
				toastr.error({
					title: 'Could not save custom category setting',
					body: res.errors.join(', '),
				});
			}

			return res;
		});
	},
	(args) => args
);
